import React from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Button,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, Snackbar, Divider, Tooltip, Link, Pagination, List, ListItem,
} from '@mui/material';
import { Email as EmailIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import PageLayout from '../components/common/PageLayout';
import { get, post } from '../utils/api';

const money = (n) => `$${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LifeMembershipEligibilityPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [report, setReport] = React.useState({ threshold: 30000, years: 3, count: 0, data: [] });

  // email dialog state
  const [dlg, setDlg] = React.useState(null); // { donor, subject, html, text }
  const [dlgLoading, setDlgLoading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [snack, setSnack] = React.useState('');

  // exclusions dialog
  const [exclOpen, setExclOpen] = React.useState(false);
  const [exclLoading, setExclLoading] = React.useState(false);
  const [excl, setExcl] = React.useState(null);

  // qualifying-donations drill-down dialog
  const [don, setDon] = React.useState(null); // { donor, page, total, sum, data }
  const [donLoading, setDonLoading] = React.useState(false);

  const openExclusions = async () => {
    setExclOpen(true);
    if (excl) return;
    setExclLoading(true);
    try { setExcl(await get('/api/manage/life-membership-eligibility/exclusions')); }
    catch (e) { setSnack(e?.data?.error || 'Failed to load exclusions'); }
    finally { setExclLoading(false); }
  };

  const openDonations = async (donor, page = 1) => {
    setDon({ donor, page, total: 0, sum: 0, data: [] });
    setDonLoading(true);
    try {
      const r = await get(`/api/manage/life-membership-eligibility/donations?donor_key=${encodeURIComponent(donor.donor_key)}&page=${page}&per_page=25`);
      setDon({ donor, page, total: r.total || 0, sum: r.sum || 0, data: r.data || [] });
    } catch (e) { setSnack(e?.data?.error || 'Failed to load donations'); setDon(null); }
    finally { setDonLoading(false); }
  };

  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await get('/api/manage/life-membership-eligibility');
      setReport(r || { data: [] });
    } catch (e) {
      setError(e?.data?.error || e?.message || 'Failed to load report');
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const openEmail = async (donor) => {
    if (!donor.email) { setSnack('This donor has no email on file.'); return; }
    setDlg({ donor, subject: '', html: '', text: '' });
    setDlgLoading(true);
    try {
      const p = await post('/api/manage/life-membership-eligibility/email/preview', {
        name: donor.name, amount: donor.total, memberships: donor.memberships, years: report.years,
      });
      setDlg({ donor, subject: p.subject || '', html: p.html || '', text: p.text || '' });
    } catch (e) {
      setSnack(e?.data?.error || 'Failed to build the email preview');
      setDlg(null);
    } finally { setDlgLoading(false); }
  };

  const sendEmail = async () => {
    if (!dlg) return;
    setSending(true);
    try {
      const r = await post('/api/manage/life-membership-eligibility/email/send', {
        to: dlg.donor.email, subject: dlg.subject, html: dlg.html, text: dlg.text,
      });
      setSnack(`Eligibility email sent to ${r.to} (cc ${r.cc}).`);
      setDlg(null);
    } catch (e) {
      setSnack(e?.data?.error || 'Failed to send the email');
    } finally { setSending(false); }
  };

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Life-membership Eligibility</Typography>
          <Typography variant="body2" color="text.secondary">
            Donors whose qualifying donations (completed orders, last {report.years} years) total{' '}
            {money(report.threshold)} or more. Excludes fees, merchandise, rentals, classes, prasadam, and
            other non-donation items. {money(report.threshold)} = 1 eligible family member; {money(report.threshold * 2)} = 2, and so on.
          </Typography>
          <Link component="button" variant="body2" onClick={openExclusions} sx={{ mt: 0.5, display: 'inline-block' }}>
            View exclusion list &rarr;
          </Link>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} variant="outlined" size="small">Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : report.data.length === 0 ? (
          <Box sx={{ p: 3 }}><Typography color="text.secondary">No donors meet the {money(report.threshold)} threshold in this window.</Typography></Box>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
              <Typography variant="body2"><strong>{report.count}</strong> eligible donor{report.count === 1 ? '' : 's'}</Typography>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Donor</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Qualifying donations</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Eligible members</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.data.map((d) => (
                    <TableRow key={d.donor_key} hover>
                      <TableCell>{d.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{d.email || <em style={{ color: '#b00' }}>no email</em>}</Typography>
                        {d.phone && <Typography variant="caption" color="text.secondary">{d.phone}</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        <Tooltip title="View the donations behind this total">
                          <Link component="button" onClick={() => openDonations(d)} sx={{ fontWeight: 600 }}>{money(d.total)}</Link>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center"><Chip size="small" color="primary" label={d.memberships} /></TableCell>
                      <TableCell align="right">
                        <Tooltip title={d.email ? 'Preview & send eligibility email' : 'No email on file'}>
                          <span>
                            <Button size="small" variant="outlined" startIcon={<EmailIcon />} disabled={!d.email} onClick={() => openEmail(d)}>
                              Email
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Paper>

      {/* Preview + edit + send dialog */}
      <Dialog open={!!dlg} onClose={() => !sending && setDlg(null)} maxWidth="md" fullWidth>
        <DialogTitle>Eligibility email — {dlg?.donor?.name} <Typography component="span" variant="body2" color="text.secondary">({dlg?.donor?.email})</Typography></DialogTitle>
        <DialogContent dividers>
          {dlgLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
          ) : dlg && (
            <>
              <Typography variant="caption" color="text.secondary">CC: icct@icctmemphis.org · Edit the wording below before sending.</Typography>
              <TextField label="Subject" fullWidth size="small" sx={{ my: 1.5 }} value={dlg.subject}
                onChange={(e) => setDlg({ ...dlg, subject: e.target.value })} />
              <TextField label="Body (HTML)" fullWidth multiline minRows={10} value={dlg.html}
                onChange={(e) => setDlg({ ...dlg, html: e.target.value })}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
              <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">Preview</Typography></Divider>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, bgcolor: '#fff' }}
                dangerouslySetInnerHTML={{ __html: dlg.html }} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)} disabled={sending}>Cancel</Button>
          <Button onClick={sendEmail} variant="contained" startIcon={<EmailIcon />} disabled={sending || dlgLoading || !dlg?.html}>
            {sending ? 'Sending…' : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exclusion list dialog */}
      <Dialog open={exclOpen} onClose={() => setExclOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>What's excluded from qualifying donations</DialogTitle>
        <DialogContent dividers>
          {exclLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
          ) : excl && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                These do <b>not</b> count toward the {money(excl.threshold)} threshold. Internal/placeholder donor emails are also excluded: {(excl.excluded_emails || []).join(', ')}.
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Excluded categories</Typography>
              {(excl.categories || []).map((cat) => (
                <Box key={cat.id} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{cat.name}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">(#{cat.id} · {cat.products.length} product{cat.products.length === 1 ? '' : 's'})</Typography>
                  </Typography>
                  {cat.products.length > 0 && (
                    <List dense disablePadding sx={{ pl: 2 }}>
                      {cat.products.map((p) => (
                        <ListItem key={p.id} disableGutters sx={{ py: 0 }}>
                          <Typography variant="body2" color="text.secondary">• {p.name} <span style={{ opacity: 0.55 }}>#{p.id}</span></Typography>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              ))}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Excluded products (inside donation categories, incl. variations)</Typography>
              {(excl.products || []).map((p) => (
                <Box key={p.id} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">(#{p.id})</Typography>
                  </Typography>
                  {p.variations.length > 0 && (
                    <List dense disablePadding sx={{ pl: 2 }}>
                      {p.variations.map((v) => (
                        <ListItem key={v.id} disableGutters sx={{ py: 0 }}>
                          <Typography variant="body2" color="text.secondary">• {v.name}</Typography>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setExclOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Qualifying-donations drill-down dialog */}
      <Dialog open={!!don} onClose={() => setDon(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Qualifying donations — {don?.donor?.name}
          <Typography component="span" variant="body2" color="text.secondary"> · {money(don?.donor?.total || 0)} total</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {donLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
          ) : don && (
            <>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Order #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="center">Qty</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {don.data.map((r, i) => (
                      <TableRow key={`${r.order_id}-${i}`} hover>
                        <TableCell>{r.order_number || r.woo_order_id || r.order_id}</TableCell>
                        <TableCell>{r.date_created_gmt ? new Date(r.date_created_gmt).toLocaleDateString() : ''}</TableCell>
                        <TableCell>{r.name || '(item)'}</TableCell>
                        <TableCell align="center">{r.quantity}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(r.total_num)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              {don.total > 25 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                  <Pagination count={Math.ceil(don.total / 25)} page={don.page} size="small" onChange={(_, p) => openDonations(don.donor, p)} />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {don.total} qualifying line item{don.total === 1 ? '' : 's'}.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDon(null)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack('')} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageLayout>
  );
}
